import { convex } from "@/lib/convexClient";
import { api } from "../../convex/_generated/api";
import { generateClientThumbnails, uploadClientThumbnails } from "./clientThumbnailGenerator";
import { resolveStoragePathSegments, type StoragePathSegments } from "@/utils/storagePaths";

type PanoramaLike = {
  id: string;
  name: string;
  private_file_url?: string | null;
  file_url?: string | null;
  thumbnail_url?: string | null;
  thumbnail_url_webp?: string | null;
};

/**
 * Check if a thumbnail URL is accessible and not expired
 */
export const checkThumbnailAvailability = async (url: string): Promise<boolean> => {
  // Skip problematic S3 URLs to prevent 403 errors
  if (url.includes("crafter.learn.360creators.com") && url.includes("thumbnail_")) {
    console.log("⏭️ Skipping known problematic S3 URL in thumbnailRegenerator:", url);
    return false;
  }

  try {
    const response = await fetch(url, { method: "HEAD" });
    return response.ok;
  } catch (error) {
    console.warn("Thumbnail availability check failed:", error);
    return false;
  }
};

/**
 * Refresh expired panorama URL
 */
const refreshPanoramaUrl = async (
  panorama: PanoramaLike,
  projectId: string,
): Promise<string | null> => {
  try {
    const sourceUrl = panorama.private_file_url || panorama.file_url;
    if (!sourceUrl) return null;

    const signedMarker = "/storage/v1/object/sign/";
    const publicMarker = "/storage/v1/object/public/";
    let filePath: string | null = null;

    if (sourceUrl.includes(signedMarker)) {
      filePath =
        sourceUrl
          .split(signedMarker)[1]
          ?.split("?")[0]
          ?.replace(/^staging\//, "") || null;
    } else if (sourceUrl.includes(publicMarker)) {
      filePath =
        sourceUrl
          .split(publicMarker)[1]
          ?.split("?")[0]
          ?.replace(/^staging\//, "") || null;
    }

    if (filePath) {
      // Generate a new cloud signed URL
      const { StorageService } = await import("@/services/storageService");
      const result = await StorageService.getPrivateUrl("staging", filePath);

      if (result.error || !result.data.signedUrl) {
        console.error("Failed to refresh panorama URL:", result.error);
        return null;
      }

      const newUrl = result.data.signedUrl;

      // Update the panorama in database
      await convex.mutation(api.panoramas.update, {
        id: panorama.id,
        private_file_url: newUrl,
      });

      return newUrl;
    }

    // If it's an S3 URL, try S3 refresh
    if (sourceUrl.includes("amazonaws.com") || sourceUrl.includes("s3.")) {
      try {
        const { StorageServiceFactory } = await import("@/services/storage/StorageServiceFactory");
        const project = await convex.query(api.projects.get, { id: projectId });
        const craftspaceId = project?.craftspace_id || "default";
        const storageService = await StorageServiceFactory.createStorageService(craftspaceId);

        const urlObj = new URL(sourceUrl);
        const pathname = urlObj.pathname.replace(/^\/+/, "");
        const segments = pathname.split("/").filter(Boolean);
        const filePath = segments.length > 1 ? segments.slice(1).join("/") : pathname;

        const newUrl = await storageService.getFileUrl(filePath);
        if (newUrl) {
          await convex.mutation(api.panoramas.update, {
            id: panorama.id,
            private_file_url: newUrl,
          });

          return newUrl;
        }
      } catch (s3Error) {
        console.error("S3 URL refresh failed:", s3Error);
      }
    }

    return null;
  } catch (error) {
    console.error("Error refreshing panorama URL:", error);
    return null;
  }
};

/**
 * Regenerate thumbnails for a panorama if they're missing or expired
 */
export const regeneratePanoramaThumbnails = async (
  panorama: PanoramaLike,
  projectId: string,
  craftspaceId: string,
): Promise<{
  success: boolean;
  thumbnail_url?: string;
  thumbnail_url_webp?: string;
  error?: string;
}> => {
  try {
    // Check if we have a valid source image URL
    let sourceUrl = panorama.private_file_url || panorama.file_url;
    if (!sourceUrl) {
      return { success: false, error: "No source image URL available" };
    }

    // Try to fetch the original image
    let imageResponse = await fetch(sourceUrl);

    // If the source image URL is expired, try to refresh it
    if (!imageResponse.ok && imageResponse.status === 400) {
      const refreshedUrl = await refreshPanoramaUrl(panorama, projectId);

      if (refreshedUrl) {
        sourceUrl = refreshedUrl;
        imageResponse = await fetch(sourceUrl);
      }
    }

    if (!imageResponse.ok) {
      throw new Error(
        `Failed to fetch source image: ${imageResponse.status} ${imageResponse.statusText}`,
      );
    }

    const imageBlob = await imageResponse.blob();
    const file = new File([imageBlob], panorama.name, { type: imageBlob.type });

    // Generate thumbnails
    const thumbnails = await generateClientThumbnails(file, 1024, 512);

    // Create safe panorama name for storage
    const panoramaName = panorama.name.replace(/[^a-zA-Z0-9-_]/g, "_").toLowerCase();

    let storageSegments: StoragePathSegments | undefined;
    try {
      const project = await convex.query(api.projects.get, { id: projectId });
      const projectData = project?.data as Record<string, unknown> | null;
      if (project) {
        storageSegments = resolveStoragePathSegments({
          craftspaceId,
          projectId: project.id,
          projectName: project.name,
          projectData,
          storagePath:
            typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
        });
      }
    } catch {
      storageSegments = undefined;
    }

    // Upload thumbnails
    const thumbnailUrls = await uploadClientThumbnails(
      thumbnails,
      craftspaceId,
      projectId,
      panoramaName,
      storageSegments,
    );

    // Update panorama record in database
    await convex.mutation(api.panoramas.update, {
      id: panorama.id,
      thumbnail_url: thumbnailUrls.thumbnail_url,
      thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp,
      processing_status: "completed",
    });

    return {
      success: true,
      thumbnail_url: thumbnailUrls.thumbnail_url,
      thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp,
    };
  } catch (error) {
    const safeError = error instanceof Error ? error : new Error("Unknown error occurred");
    console.error(`❌ Failed to regenerate thumbnails for ${panorama.name}:`, error);
    return {
      success: false,
      error: safeError.message,
    };
  }
};

/**
 * Check if source panorama file exists
 */
const checkSourcePanoramaExists = async (panorama: PanoramaLike): Promise<boolean> => {
  try {
    const sourceUrl = panorama.private_file_url || panorama.file_url;
    if (!sourceUrl) return false;

    // Skip problematic S3 URLs to prevent 403 errors
    if (sourceUrl.includes("crafter.learn.360creators.com") && sourceUrl.includes("thumbnail_")) {
      console.log("⏭️ Skipping known problematic S3 URL in checkSourcePanoramaExists:", sourceUrl);
      return false;
    }

    const response = await fetch(sourceUrl, { method: "HEAD" });
    return response.ok;
  } catch (error) {
    console.warn(`Source panorama check failed for ${panorama.name}:`, error);
    return false;
  }
};

/**
 * Refresh expired thumbnail URLs
 */
const refreshThumbnailUrls = async (
  panorama: PanoramaLike,
  _projectId: string,
  _craftspaceId: string,
): Promise<{ thumbnail_url?: string; thumbnail_url_webp?: string } | null> => {
  try {
    const { StorageService } = await import("@/services/storageService");

    let newThumbnailUrl = null;
    let newThumbnailWebpUrl = null;

    // Try to refresh thumbnail URLs if they exist but are expired
    if (panorama.thumbnail_url?.includes("/sign/")) {
      try {
        // Extract file path from signed URL
        const urlParts = panorama.thumbnail_url.split("/");
        const stagingIndex = urlParts.indexOf("staging");
        if (stagingIndex !== -1) {
          const filePath = urlParts
            .slice(stagingIndex + 1)
            .join("/")
            .split("?")[0];
          const result = await StorageService.getPrivateUrl("staging", filePath);
          if (result.data?.signedUrl) {
            newThumbnailUrl = result.data.signedUrl;
          }
        }
      } catch (error) {
        console.warn(`Failed to refresh thumbnail URL for ${panorama.name}:`, error);
      }
    }

    if (panorama.thumbnail_url_webp?.includes("/sign/")) {
      try {
        // Extract file path from signed URL
        const urlParts = panorama.thumbnail_url_webp.split("/");
        const stagingIndex = urlParts.indexOf("staging");
        if (stagingIndex !== -1) {
          const filePath = urlParts
            .slice(stagingIndex + 1)
            .join("/")
            .split("?")[0];
          const result = await StorageService.getPrivateUrl("staging", filePath);
          if (result.data?.signedUrl) {
            newThumbnailWebpUrl = result.data.signedUrl;
          }
        }
      } catch (error) {
        console.warn(`Failed to refresh WebP thumbnail URL for ${panorama.name}:`, error);
      }
    }

    // If we got new URLs, update the database
    if (newThumbnailUrl || newThumbnailWebpUrl) {
      const updateData: Partial<Pick<PanoramaLike, "thumbnail_url" | "thumbnail_url_webp">> = {};
      if (newThumbnailUrl) updateData.thumbnail_url = newThumbnailUrl;
      if (newThumbnailWebpUrl) updateData.thumbnail_url_webp = newThumbnailWebpUrl;

      await convex.mutation(api.panoramas.update, {
        id: panorama.id,
        ...updateData,
      });

      return { thumbnail_url: newThumbnailUrl, thumbnail_url_webp: newThumbnailWebpUrl };
    }

    return null;
  } catch (error) {
    console.error(`Error refreshing thumbnail URLs for ${panorama.name}:`, error);
    return null;
  }
};

/**
 * Check and regenerate thumbnails for a panorama if needed
 */
export const checkAndRegenerateThumbnails = async (
  panorama: PanoramaLike,
  projectId: string,
  craftspaceId: string,
): Promise<{ regenerated: boolean; thumbnail_url?: string; thumbnail_url_webp?: string }> => {
  try {
    // Check if thumbnails exist and are accessible
    const hasThumbnail =
      panorama.thumbnail_url && (await checkThumbnailAvailability(panorama.thumbnail_url));
    const hasWebPThumbnail =
      panorama.thumbnail_url_webp &&
      (await checkThumbnailAvailability(panorama.thumbnail_url_webp));

    // If we have working thumbnails, no need to do anything
    if (hasThumbnail || hasWebPThumbnail) {
      return { regenerated: false };
    }

    // Try to refresh expired URLs first
    const refreshedUrls = await refreshThumbnailUrls(panorama, projectId, craftspaceId);

    if (refreshedUrls) {
      return {
        regenerated: true,
        thumbnail_url: refreshedUrls.thumbnail_url,
        thumbnail_url_webp: refreshedUrls.thumbnail_url_webp,
      };
    }

    // If URL refresh failed, check if source exists and regenerate
    const sourceExists = await checkSourcePanoramaExists(panorama);
    if (!sourceExists) {
      console.warn(
        `⚠️ Source panorama file missing for: ${panorama.name} - cannot regenerate thumbnails`,
      );
      return { regenerated: false };
    }

    // Regenerate thumbnails from source
    const result = await regeneratePanoramaThumbnails(panorama, projectId, craftspaceId);

    if (result.success) {
      return {
        regenerated: true,
        thumbnail_url: result.thumbnail_url,
        thumbnail_url_webp: result.thumbnail_url_webp,
      };
    } else {
      return { regenerated: false };
    }
  } catch (error) {
    console.error(`❌ Error checking/regenerating thumbnails for ${panorama.name}:`, error);
    return { regenerated: false };
  }
};
